/* RESPONSIBLE TEAM: team-self-serve */
import Service, { inject as service } from '@ember/service';
import {
  getDefaultCategories,
  getFeatures,
  type IntercomCapabilityFeature,
  type Category,
} from '@intercom/billing-core/marketing-features';
import type AppService from 'embercom/services/app-service';

export default class MarketingFeaturesService extends Service {
  @service declare appService: AppService;

  get features(): Record<string, IntercomCapabilityFeature> {
    let categories = this.getCategories(this.appService.app.canSeeAiInsightsAddon);
    let allFeatures = getFeatures();

    let featureKeys = Object.values(categories).flatMap((category) => {
      let categoryFeatures = category.features ?? [];
      let subGroupFeatures = category.subGroups
        ? Object.values(category.subGroups).flatMap((subGroup) => subGroup.features)
        : [];
      return [...categoryFeatures, ...subGroupFeatures];
    });

    let result: Record<string, IntercomCapabilityFeature> = {};
    featureKeys.forEach((key) => {
      if (allFeatures[key]) {
        result[key] = allFeatures[key];
      }
    });

    return result;
  }

  getFeaturesByPlanId(planIds: number | number[]): Record<string, IntercomCapabilityFeature> {
    let ids = Array.isArray(planIds) ? planIds : [planIds];
    let filteredEntries = Object.entries(this.features).filter(([, feature]) =>
      ids.some((id) => feature.p5PlanIds.includes(String(id))),
    );
    return Object.fromEntries(filteredEntries);
  }

  isFeatureInPlan(featureKey: string, planId: number): boolean {
    let feature = this.features[featureKey];
    return feature ? feature.p5PlanIds.includes(String(planId)) : false;
  }

  getCategories(isInsightsEnabled = false): Record<string, Category> {
    return getDefaultCategories(isInsightsEnabled);
  }
}

declare module '@ember/service' {
  interface Registry {
    'marketing-features-service': MarketingFeaturesService;
  }
}
